import { createApi } from '@reduxjs/toolkit/query/react'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { ProducerDictionary } from 'store/app/types'
import { PersonalizedProducersSimulationData, Producer } from 'types/producer'
import { isSoldOutWithCapacity } from 'utils/producers'
import { Language } from 'types/language'
import { PersonalizedProducersOffers } from 'store/customer/types'
import { AllProducts } from 'types/product-data'
import { GetPersonalizedProducersResponse, NextApiResponseBody } from 'types/bolt-api/request'
import { GetPlentyEnvCheckResponse, GetPlentyPaymentStatusResponse } from 'store/api/types'
import { GetPartnerContactsResponse } from 'lib/partners/types'
import { GetTownsResponse } from 'types/towns'
import { SimulatePriceRequest, SimulatePriceResponse } from 'features/simulation/types'

export const boltApi = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: '/api' }),
  endpoints: (builder) => ({
    getPersonalizedOffer: builder.query<PersonalizedProducersOffers, PersonalizedProducersSimulationData>({
      query: (simulationData) => ({
        body: {
          expectedProducers: { nearest: true, popular: true, recent: true },
          ...simulationData
        },
        method: 'POST',
        url: '/producers/personalized'
      }),
      transformResponse: ({ data }: NextApiResponseBody<GetPersonalizedProducersResponse>): PersonalizedProducersOffers => {
        if (!data) {
          return {
            [Language.DUTCH]: {},
            [Language.FRENCH]: {}
          }
        }

        const { popular: popularNL, nearest: nearestNL, recent: recentNL } = data[Language.DUTCH]
        const { popular: popularFR, nearest: nearestFR, recent: recentFR } = data[Language.FRENCH]

        return {
          [Language.DUTCH]: { nearest: nearestNL?.slug, popular: popularNL?.slug, recent: recentNL?.slug },
          [Language.FRENCH]: { nearest: nearestFR?.slug, popular: popularFR?.slug, recent: recentFR?.slug }
        }
      }
    }),

    simulatePrice: builder.query<SimulatePriceResponse | null, SimulatePriceRequest>({
      query: (simulatePriceRequest) => ({
        body: simulatePriceRequest,
        method: 'POST',
        url: '/website/price-simulation'
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<SimulatePriceResponse>) => {
        return success && data ? data : null
      }
    }),

    getProducers: builder.query<ProducerDictionary | undefined, void>({
      query: () => ({
        method: 'GET',
        url: '/producers?enabled=true'
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<Producer[]>): ProducerDictionary | undefined => {
        if (success && data?.length) {
          const filteredProducers = data.filter((p: Producer) => !isSoldOutWithCapacity(p))
          return filteredProducers.reduce((acc: ProducerDictionary, producer: Producer) => {
            acc[producer.slug] = producer
            return acc
          }, {})
        }
      }
    }),

    getProducts: builder.query<AllProducts | null, void>({
      query: () => ({
        method: 'GET',
        url: '/products'
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<AllProducts>): AllProducts | null => {
        return success && data ? data : null
      }
    }),

    getPlentyStatus: builder.query<GetPlentyPaymentStatusResponse | null, string>({
      query: (plentyOpportunityId) => ({
        method: 'GET',
        url: `/plenty/prospects/${plentyOpportunityId}/payment-status`
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<GetPlentyPaymentStatusResponse>) => {
        return success && data ? data : null
      }
    }),

    getCheckIsPlentyInvestor: builder.query<GetPlentyEnvCheckResponse | null, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `/contacts/plenty-env-check?opportunityId=${opportunityId}`
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<GetPlentyEnvCheckResponse>) => {
        return success && data ? data : null
      }
    }),

    getTowns: builder.query<GetTownsResponse | null, string>({
      query: (postalCode) => ({
        method: 'GET',
        url: `/towns/${postalCode}`
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<GetTownsResponse>) => {
        return success && data ? data : null
      }
    }),

    getPartnerContacts: builder.query<GetPartnerContactsResponse, { partnerId: string; email: string }>({
      query: ({ partnerId, email }) => ({
        method: 'GET',
        url: `/partners/${partnerId}/contacts?email=${encodeURIComponent(email)}`
      }),
      transformResponse: ({ success, data }: NextApiResponseBody<GetPartnerContactsResponse>) => {
        return success && data ? data : { contacts: [] }
      }
    })
  }),
  reducerPath: 'boltApi'
})

export const {
  useGetPersonalizedOfferQuery,
  useGetProducersQuery,
  useGetProductsQuery,
  useGetPlentyStatusQuery,
  useGetCheckIsPlentyInvestorQuery,
  useLazyGetCheckIsPlentyInvestorQuery,
  useGetPartnerContactsQuery,
  useLazyGetPartnerContactsQuery,
  useGetTownsQuery,
  useSimulatePriceQuery,
  useLazySimulatePriceQuery
} = boltApi
